// Extracted from original/src/App.tsx:41485-41596 (ADR-08 §2.1): the Settings section — category top bar
// (:41488-41556), the category → card-key map (:41567-41590) and the 2-column cards grid (:41558-41594).
// Card frames (icon + title) per key come from `renderSettingsSection` (:13075-16800, see SETTINGS_CARDS).
// Cut: `settingsOrder` / `settingsCategory` state and localStorage (the container holds them, D-123
// `ui.settings_order`), `renderSettingsSection` bodies other than the appearance, security and users cards.
import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { Bell, Coins, CreditCard, FileText, Grip, Palette, Settings as SettingsIcon, Shield, ShieldCheck, Store, TrendingUp, User, Users, Wallet } from 'lucide-react';
import { DynamicIcon } from '../components/HelperComponents.js';
import { SettingsCard } from '../components/SettingsCard.js';
import { useT } from '../i18n-context.js';

export type SettingsCategory = 'display' | 'finance' | 'users' | 'system';

export type SettingsCardKey =
  | 'layout' | 'notifications' | 'security' | 'users' | 'wallets' | 'company' | 'office_storefront' | 'capital'
  | 'sub_safes_management' | 'partners_management' | 'digital_profits_management' | 'super_currencies' | 'maintenance' | 'menu_customization'
  | 'subscription'; // [C27] new card (D-261, V-54)

/** App.tsx:1781-1797 default order. */
export const SETTINGS_DEFAULT_ORDER: readonly SettingsCardKey[] = [
  'layout', 'notifications', 'security', 'users', 'wallets', 'company', 'office_storefront', 'capital',
  'sub_safes_management', 'partners_management', 'digital_profits_management', 'super_currencies', 'maintenance', 'menu_customization',
  'subscription', // [C27] appended: a viewer's saved order keeps its old cards in place (D-124)
];

/** App.tsx:41567-41590. */
export const SETTINGS_CATEGORY_CARDS: Readonly<Record<SettingsCategory, readonly SettingsCardKey[]>> = {
  display: ['layout', 'menu_customization'],
  finance: ['capital', 'wallets', 'sub_safes_management', 'partners_management', 'digital_profits_management', 'super_currencies'],
  users: ['users', 'security'],
  system: ['company', 'office_storefront', 'notifications', 'maintenance', 'subscription'],
};

/** Icon and title of every card frame, as in `renderSettingsSection`; `titleKey` is an i18n key. */
export const SETTINGS_CARDS: Readonly<Record<SettingsCardKey, { readonly icon: (cls: string) => ReactNode; readonly iconClass: string; readonly titleKey: string }>> = {
  layout: { icon: (c) => <Palette size={24} className={c} />, iconClass: 'text-purple-600 dark:text-purple-400', titleKey: 'layout' }, // :13669-13681
  notifications: { icon: (c) => <Bell size={24} className={c} />, iconClass: 'text-amber-500 dark:text-amber-400', titleKey: 'settings.card_notifications' }, // :13773-13793
  security: { icon: (c) => <ShieldCheck size={24} className={c} />, iconClass: 'text-rose-600 dark:text-rose-400', titleKey: 'settings.card_security' }, // :14266-14282 (title per D-133)
  users: { icon: (c) => <User size={24} className={c} />, iconClass: 'text-emerald-600 dark:text-emerald-400', titleKey: 'settings.card_users' }, // :14811-14827
  wallets: { icon: (c) => <Grip size={24} className={c} />, iconClass: 'text-orange-600 dark:text-orange-400', titleKey: 'settings.card_boxes' }, // :14944-14960
  company: { icon: (c) => <FileText size={24} className={c} />, iconClass: 'text-blue-600 dark:text-blue-400', titleKey: 'settings.card_company' }, // :15041-15061
  office_storefront: { icon: (c) => <Store size={24} className={c} />, iconClass: 'text-blue-600 dark:text-blue-400', titleKey: 'settings.card_storefront' }, // OfficeStorefrontSettings.tsx:330-333
  capital: { icon: (c) => <Wallet size={24} className={c} />, iconClass: 'text-emerald-600 dark:text-emerald-400', titleKey: 'capital' }, // :15418-15434
  sub_safes_management: { icon: (c) => <Shield size={24} className={c} />, iconClass: 'text-emerald-600 dark:text-emerald-400', titleKey: 'settings.card_sub_safes' }, // :15545-15556
  partners_management: { icon: (c) => <Users size={24} className={c} />, iconClass: 'text-amber-500', titleKey: 'settings.card_partners' }, // :16533-16536
  digital_profits_management: { icon: (c) => <Coins size={24} className={c} />, iconClass: 'text-amber-500', titleKey: 'settings.card_digital_profits' }, // :16372-16379
  super_currencies: { icon: (c) => <Coins size={24} className={c} />, iconClass: 'text-rose-600 dark:text-rose-400', titleKey: 'settings.card_currencies' }, // :16739-16748
  maintenance: { icon: (c) => <SettingsIcon size={24} className={c} />, iconClass: 'text-indigo-600 dark:text-indigo-400', titleKey: 'systemMaintenance' }, // :16768-16784
  menu_customization: { icon: (c) => <Grip size={24} className={c} />, iconClass: 'text-indigo-600 dark:text-indigo-400', titleKey: 'menuCustomization' }, // :13079-13087
  subscription: { icon: (c) => <CreditCard size={24} className={c} />, iconClass: 'text-amber-500 dark:text-amber-400', titleKey: 'settings.card_subscription' }, // [C27] new (V-54); icon of the old gate modal's activate form
};

/** A settings card frame with the old icon / title; `customIcon` = per-user icon override (D-124). */
export function SettingsCardFrame({ cardKey, customIcon, children }: { cardKey: SettingsCardKey; customIcon?: string; children: ReactNode }) {
  const t = useT();
  const def = SETTINGS_CARDS[cardKey];
  const icon = customIcon ? <DynamicIcon name={customIcon} size={24} className={def.iconClass} /> : def.icon(def.iconClass);
  // No wrapper: the card is the grid item and stretches to the row height (App.tsx:41558-41594 renders SettingsCard directly).
  return (
    <SettingsCard testId={`settings-card-${cardKey}`} icon={icon} title={t(def.titleKey)}>
      {children}
    </SettingsCard>
  );
}

/** Placeholder body for the cards whose content arrives in B4 (frame + slot). */
export function SettingsCardPlaceholder() {
  const t = useT();
  return (
    <div className="p-4 bg-gray-50 dark:bg-[#05070a] rounded-xl text-center border border-gray-100 dark:border-[#30363d]">
      <p className="text-gray-500 dark:text-[#8b949e] text-sm font-bold">{t('settings.card_placeholder')}</p>
    </div>
  );
}

export interface SettingsProps {
  readonly category: SettingsCategory;
  readonly onCategoryChange: (category: SettingsCategory) => void;
  /** The cards of the active category, already filtered and ordered by the container. */
  readonly children: ReactNode;
}

const CATEGORIES: readonly { id: SettingsCategory; labelKey: string; icon: typeof Palette }[] = [
  { id: 'display', labelKey: 'settings.cat_display', icon: Palette },
  { id: 'finance', labelKey: 'settings.cat_finance', icon: Coins },
  { id: 'users', labelKey: 'settings.cat_users', icon: Users },
  { id: 'system', labelKey: 'settings.cat_system', icon: SettingsIcon },
];

export function Settings(props: SettingsProps) {
  const t = useT();
  return (
    <div className="space-y-6 w-full" data-testid="settings">
      {/* Category Top Bar */}
      <div className="bg-white dark:bg-[#0d1117] p-4 rounded-3xl border border-slate-200/50 dark:border-[#30363d] shadow-premium flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-primary-500/10 text-primary-500 rounded-2xl">
            <SettingsIcon
              size={22}
              className="animate-[spin_10s_linear_infinite]"
            />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-800 dark:text-[#f0f6fc]">
              {t('settings.title')}
            </h2>
            <p className="text-[10px] font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-widest mt-0.5">
              {t('settings.subtitle')}
            </p>
          </div>
        </div>
        {/* Categories Selector */}
        <div className="flex flex-wrap items-center gap-2">
          {CATEGORIES.map((cat) => {
            const active = props.category === cat.id;
            const IconComp = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => props.onCategoryChange(cat.id)}
                data-testid={`settings-cat-${cat.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black transition-all duration-300 border cursor-pointer active:scale-95 ${
                  active
                    ? "bg-primary-500 text-white border-primary-500 shadow-lg shadow-primary-500/20"
                    : "bg-slate-50 dark:bg-white/5 text-slate-600 dark:text-slate-400 border-slate-200/50 dark:border-[#30363d] hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-[#f0f6fc]"
                }`}
              >
                <IconComp size={16} />
                <span>
                  {t(cat.labelKey)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Grid of Settings Cards */}
      <motion.div
        key={`settings-${props.category}`}
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -15 }}
        transition={{ duration: 0.3 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-8"
      >
        {props.children}
      </motion.div>
    </div>
  );
}
